import Stack, { StackProps } from '@mui/material/Stack';
import Typography, { TypographyProps } from '@mui/material/Typography';

/**
 * Props for the {@link TwoLineCell} component.
 */
interface TwoLineCellProps extends Omit<StackProps, 'children'> {
  /**
   * The first line of text to display.
   * Will not render if undefined, null, or only whitespace.
   *
   * @type {string | undefined}
   */
  line1?: string;

  /**
   * Additional props to apply to the Typography component rendering `line1`.
   * Overrides default styling like variant, noWrap, or lineHeight if specified.
   *
   * @type {TypographyProps | undefined}
   */
  line1Props?: TypographyProps;

  /**
   * The second line of text to display.
   * Will not render if undefined, null, or only whitespace.
   *
   * @type {string | undefined}
   */
  line2?: string;

  /**
   * Additional props to apply to the Typography component rendering `line2`.
   * Overrides default styling like variant, noWrap, or lineHeight if specified.
   *
   * @type {TypographyProps | undefined}
   */
  line2Props?: TypographyProps;

  /**
   * The horizontal alignment of the text within both Typography lines.
   * This will be overridden by `align` in `line1Props` or `line2Props` if those are defined.
   *
   * @type {'inherit' | 'left' | 'center' | 'right' | 'justify' | undefined}
   */
  textAlign?: TypographyProps['align'];
}

/**
 * Renders up to two vertically stacked lines of text, typically used in MUI DataGrid cells.
 *
 * Each line can be individually styled using MUI Typography props. Text is truncated with
 * ellipsis (`noWrap`) if it overflows. If a line is empty or only whitespace, it is not rendered.
 *
 * @param {TwoLineCellProps} props - The props used to configure the layout and content.
 * @param {string} [props.line1] - First line of text content.
 * @param {TypographyProps} [props.line1Props] - Optional styling props for the first line.
 * @param {string} [props.line2] - Second line of text content.
 * @param {TypographyProps} [props.line2Props] - Optional styling props for the second line.
 * @param {TypographyProps['align']} [props.textAlign] - Horizontal alignment for both lines.
 *
 * @returns {JSX.Element} A React element containing up to two styled lines of vertically stacked text.
 */
export function TwoLineCell({ line1, line1Props, line2, line2Props, textAlign }: TwoLineCellProps) {
  return (
    <Stack direction="column" justifyContent="center" spacing={0.5} height="100%" overflow="hidden">
      {line1 && line1.trim().length > 0 && (
        <Typography variant="inherit" noWrap lineHeight={1} align={textAlign} {...line1Props}>
          {line1}
        </Typography>
      )}
      {line2 && line2.trim().length > 0 && (
        <Typography variant="inherit" noWrap lineHeight={1} align={textAlign} {...line2Props}>
          {line2}
        </Typography>
      )}
    </Stack>
  );
}

export default TwoLineCell;
